import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { AppShell } from "@/components/app/AppShell";
import { Button } from "@/components/ui/button";
import { LegalDisclaimer } from "@/components/common/LegalDisclaimer";
import { BookingSection } from "@/components/assessment/BookingSection";
import { GuestSignalsSection } from "@/components/assessment/GuestSignalsSection";
import { PropertySection, HostNotesSection } from "@/components/assessment/PropertySection";
import api, { getErrorMessage } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";

const DEFAULTS = {
  guest_initials: "", platform: "Airbnb", num_reviews: 0, avg_rating: 0,
  account_age: "Unknown", lead_time: "3-7 days", length_of_stay: 2, num_guests: 2,
  local_guest: "Unknown", weekend_stay: "No", public_holiday: "No",
  property_type: "Apartment", entire_or_private: "Entire home", property_max_guests: 4,
  message_quality: "Unknown", purpose: "Holiday", id_verified: "Unknown",
  security_deposit: "Unknown", host_notes: "", property_id: "none",
};

export default function NewAssessment() {
  const navigate = useNavigate();
  const [form, setForm] = useState(DEFAULTS);
  const [properties, setProperties] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let active = true;
    trackEvent("assessment_started", "/app/new");
    api.get("/properties").then((r) => { if (active) setProperties(r.data); }).catch(() => {});
    return () => { active = false; };
  }, []);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const setNum = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value === "" ? "" : Number(e.target.value) }));

  const onProperty = (val) => {
    setForm((f) => {
      const next = { ...f, property_id: val };
      const p = properties.find((x) => x.id === val);
      if (p) { next.property_type = p.property_type; next.property_max_guests = p.max_guests; }
      return next;
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    // Light client-side validation / clamping
    const rating = Number(form.avg_rating) || 0;
    if (rating < 0 || rating > 5) {
      toast.error("Average guest rating must be between 0 and 5.");
      return;
    }
    if (Number(form.num_reviews) < 0) {
      toast.error("Number of reviews can't be negative.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form };
      payload.property_id = form.property_id === "none" ? null : form.property_id;
      payload.num_reviews = Math.max(0, Number(form.num_reviews) || 0);
      payload.avg_rating = Math.min(5, Math.max(0, rating));
      payload.length_of_stay = Math.max(1, Number(form.length_of_stay) || 1);
      payload.num_guests = Math.max(1, Number(form.num_guests) || 1);
      payload.property_max_guests = Math.max(0, Number(form.property_max_guests) || 0);
      const res = await api.post("/assessments", payload);
      trackEvent("assessment_completed", "/app/new");
      toast.success("Assessment complete");
      navigate(`/app/result/${res.data.id}`);
    } catch (err) {
      toast.error(getErrorMessage(err, "Could not create assessment."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AppShell title="New Risk Assessment">
      <form onSubmit={submit} className="space-y-6 pb-24">
        <p className="text-sm text-muted-foreground">Enter the booking characteristics below. We assess the booking, never the guest — use initials only.</p>

        <BookingSection form={form} set={set} setNum={setNum} />
        <GuestSignalsSection form={form} set={set} setNum={setNum} />
        <PropertySection form={form} set={set} setNum={setNum} properties={properties} onProperty={onProperty} />
        <HostNotesSection form={form} set={set} />

        <LegalDisclaimer />

        <div className="fixed inset-x-0 bottom-0 z-20 border-t bg-background p-4 lg:left-64">
          <div className="mx-auto flex max-w-[1200px] items-center justify-between gap-4">
            <p className="hidden text-xs text-muted-foreground sm:block"><Sparkles className="mr-1 inline h-3.5 w-3.5" />Score is computed instantly. AI summary is available on Pro plans.</p>
            <Button type="submit" size="lg" disabled={submitting} className="ml-auto gap-2" data-testid="new-assessment-submit-button">
              {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Assessing...</> : "Calculate risk"}
            </Button>
          </div>
        </div>
      </form>
    </AppShell>
  );
}
